import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { Button } from '../components/ui/button';
import { Users, Wifi, Tv, Wind, Coffee, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

const WHATSAPP_NUMBER = '5594992013575';

export const Rooms = () => {
  const [roomTypes, setRoomTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRoomTypes();
  }, []);

  const loadRoomTypes = async () => {
    try {
      const data = await api.get('/api/room-types');
      setRoomTypes(data);
    } catch (error) {
      toast.error('Erro ao carregar quartos');
    } finally {
      setLoading(false);
    }
  };

  const handleWhatsAppClick = (roomName) => {
    const message = encodeURIComponent(`Olá! Gostaria de reservar um quarto no Hotel Del Rio.
Datas desejadas:
Número de hóspedes:
Tipo de quarto: ${roomName}`);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, '_blank');
  };

  const getAmenityIcon = (amenity) => {
    const lower = amenity.toLowerCase();
    if (lower.includes('wi-fi') || lower.includes('wifi')) return <Wifi className="h-4 w-4" />;
    if (lower.includes('tv')) return <Tv className="h-4 w-4" />;
    if (lower.includes('ar-condicionado') || lower.includes('ar')) return <Wind className="h-4 w-4" />;
    if (lower.includes('café') || lower.includes('cafe') || lower.includes('frigobar')) return <Coffee className="h-4 w-4" />;
    return <span className="text-xs">•</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-text-secondary">Carregando quartos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12" data-testid="rooms-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-display font-bold text-primary mb-4">
            Nossos Quartos
          </h1>
          <p className="text-text-secondary max-w-2xl mx-auto">
            Escolha o quarto perfeito para sua estadia no Hotel Del Rio
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {roomTypes.map((room) => (
            <div
              key={room.id}
              className="group relative overflow-hidden rounded-xl bg-white shadow-lg hover:shadow-xl transition-all duration-300"
              data-testid={`room-card-${room.id}`}
            >
              <div className="relative h-64 overflow-hidden">
                <img
                  src={room.images[0]}
                  alt={room.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
              </div>

              <div className="p-6">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-2xl font-display font-semibold text-primary">
                    {room.name}
                  </h3>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-accent">R$ {room.base_price.toFixed(2)}</p>
                    <p className="text-sm text-text-muted">por noite</p>
                  </div>
                </div>

                <p className="text-text-secondary mb-4 line-clamp-2">
                  {room.description}
                </p>

                <div className="flex items-center gap-2 text-sm text-text-muted mb-4">
                  <Users className="h-4 w-4" />
                  <span>Até {room.max_guests} hóspedes</span>
                </div>

                <div className="flex flex-wrap gap-2 mb-4">
                  {room.amenities.slice(0, 4).map((amenity, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-1 text-xs text-text-secondary bg-background px-2 py-1 rounded"
                    >
                      {getAmenityIcon(amenity)}
                      <span>{amenity}</span>
                    </div>
                  ))}
                  {room.amenities.length > 4 && (
                    <div className="text-xs text-text-muted px-2 py-1">
                      +{room.amenities.length - 4} mais
                    </div>
                  )}
                </div>

                <Button
                  className="w-full bg-primary hover:bg-primary/90 rounded-full"
                  onClick={() => handleWhatsAppClick(room.name)}
                  data-testid={`book-room-${room.id}`}
                >
                  <MessageCircle className="h-4 w-4 mr-2" />
                  Reservar agora pelo WhatsApp
                </Button>
              </div>
            </div>
          ))}
        </div>

        {roomTypes.length === 0 && (
          <div className="text-center py-12">
            <p className="text-text-secondary">Nenhum quarto disponível no momento.</p>
          </div>
        )}
      </div>
    </div>
  );
};
